import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, MapPin, CheckCircle, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useScrollReveal, useHashScroll } from "../lib/hooks";
import { StarRating, SectionBadge, CTABanner } from "../components/Shared";
import { FaqSection } from "../components/FaqSection";
import { PhotoReportSection } from "../components/PhotoReportSection";
import { AreasSection } from "../components/AreasSection";
import {
  PHONE_LINK, HERO_IMG, SERVICES, TRUST_POINTS, TESTIMONIALS,
  FONT_HEAD, FONT_BODY, NAVY, GOLD,
} from "../lib/constants";

const HeroBadge = () => (
  <div
    className="inline-flex items-center gap-2 mb-5 px-4 py-1.5 text-xs font-bold tracking-[0.18em] uppercase fade-in-up"
    style={{
      background: "rgba(212,168,67,0.14)",
      color: "#b8922a",
      borderLeft: `3px solid ${GOLD}`,
      fontFamily: FONT_HEAD,
    }}
  >
    <MapPin size={12} />
    Naples, FL — Licensed &amp; Insured
  </div>
);

const HeroCTAs = () => (
  <div className="flex flex-col sm:flex-row gap-4 mb-10 fade-in-up-delay-2">
    <a
      href={PHONE_LINK}
      data-testid="hero-call-btn"
      className="flex items-center justify-center gap-2 px-8 py-4 text-base font-bold transition-all duration-300 hover:opacity-90 hover:shadow-lg"
      style={{ background: NAVY, color: "#FFFFFF", fontFamily: FONT_BODY }}
    >
      <Phone size={18} />
      Call Us Now
    </a>
    <Link
      to="/contact"
      data-testid="hero-quote-btn"
      className="flex items-center justify-center gap-2 px-8 py-4 text-base font-bold border-2 transition-all duration-300 hover:bg-[#1B3A6B] hover:text-white"
      style={{ borderColor: NAVY, color: NAVY, fontFamily: FONT_BODY }}
    >
      Get a Free Quote
      <ArrowRight size={18} />
    </Link>
  </div>
);

const HeroTrustBadges = () => (
  <div className="flex flex-wrap gap-5 fade-in-up-delay-3">
    {["Licensed & Insured", "Naples, FL Based", "5-Star Service"].map((badge) => (
      <div key={badge} className="flex items-center gap-2">
        <CheckCircle size={16} style={{ color: GOLD }} />
        <span className="text-sm font-semibold" style={{ color: "#2D2D2D", fontFamily: FONT_BODY }}>
          {badge}
        </span>
      </div>
    ))}
  </div>
);

const HeroContent = () => (
  <div className="order-2 lg:order-1">
    <HeroBadge />
    <h1
      className="text-4xl sm:text-5xl lg:text-5xl font-bold mb-5 leading-tight fade-in-up-delay-1"
      style={{ fontFamily: FONT_HEAD, color: NAVY, letterSpacing: "-0.02em" }}
      data-testid="hero-headline"
    >
      House Cleaning &amp; <span style={{ color: GOLD }}>Housekeeping</span> in Naples, FL
      <br />
      <span className="text-3xl sm:text-4xl" style={{ color: "#2D2D2D" }}>
        — Licensed &amp; Insured
      </span>
    </h1>
    <p
      className="text-base md:text-lg mb-8 leading-relaxed fade-in-up-delay-2"
      style={{ color: "#6B7280", fontFamily: FONT_BODY }}
      data-testid="hero-subheadline"
    >
      Licensed, insured &amp; detail-obsessed. Serving Naples, FL with
      a team you can trust.
    </p>
    <HeroCTAs />
    <HeroTrustBadges />
  </div>
);

const HeroImage = () => (
  <div className="order-1 lg:order-2 fade-in-right">
    <div
      className="relative overflow-hidden"
      style={{ boxShadow: `16px 16px 0px ${GOLD}, 0 10px 50px rgba(27,58,107,0.18)` }}
    >
      <img
        src={HERO_IMG}
        alt="Professional house cleaning service in Naples FL — spotless modern kitchen cleaned by Cindyrella Cleaning LLC"
        className="w-full h-64 sm:h-80 lg:h-[480px] object-cover"
        data-testid="hero-image"
      />
      <div
        className="absolute bottom-0 left-0 right-0 px-6 py-4"
        style={{ background: "rgba(27,58,107,0.88)", backdropFilter: "blur(8px)" }}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-white text-sm font-bold" style={{ fontFamily: FONT_HEAD }}>
              Trusted by Naples Families & Businesses
            </div>
            <div className="flex items-center gap-2 mt-1">
              <StarRating />
              <span className="text-xs" style={{ color: "rgba(255,255,255,0.85)", fontFamily: FONT_BODY }}>
                5.0 Avg. Rating
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const HeroSection = () => (
  <section
    id="hero"
    data-testid="hero-section"
    className="pt-16 md:pt-20 flex items-center"
    style={{ background: "linear-gradient(135deg, #F0F4FF 0%, #FFFFFF 60%, #F9FAFB 100%)" }}
  >
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20 w-full">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center">
        <HeroContent />
        <HeroImage />
      </div>
    </div>
  </section>
);

const ServiceCard = ({ id, Icon, title, description, image, link, idx }) => (
  <Link
    to={link || `/services#${id}`}
    data-testid={`service-card-${idx}`}
    className="bg-white overflow-hidden group hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-xl block"
    style={{ border: "1px solid rgba(27,58,107,0.08)" }}
  >
    <div className="h-48 overflow-hidden">
      <img
        src={image}
        alt={`${title} cleaning service in Naples, FL — Cindyrella Cleaning`}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
      />
    </div>
    <div className="p-6">
      <div className="w-10 h-10 rounded-full flex items-center justify-center mb-4" style={{ background: "rgba(27,58,107,0.1)" }}>
        <Icon size={20} style={{ color: NAVY }} />
      </div>
      <h3 className="text-lg font-bold mb-2" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
        {title}
      </h3>
      <p className="text-sm leading-relaxed" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
        {description}
      </p>
      <span
        className="inline-flex items-center gap-1.5 mt-4 text-sm font-bold group-hover:gap-2.5 transition-all"
        style={{ color: GOLD, fontFamily: FONT_BODY }}
      >
        Learn More <ArrowRight size={14} />
      </span>
    </div>
  </Link>
);

const ServicesPreview = () => {
  const ref = useScrollReveal();
  return (
    <section
      id="services"
      data-testid="services-section"
      ref={ref}
      className="py-20 md:py-28 section-hidden"
      style={{ background: "#F9FAFB" }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <SectionBadge>Our Services</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
            What We Clean
          </h2>
          <p className="mt-4 text-base max-w-xl mx-auto" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
            From luxury homes to commercial spaces — we bring five-star
            standards to every space we touch.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((service, idx) => (
            <ServiceCard key={service.id} {...service} idx={idx} />
          ))}
        </div>
        <p className="mt-10 text-center text-sm" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
          Finishing a build or renovation?{" "}
          <Link to="/services/post-construction-cleaning-naples-fl" data-testid="home-post-construction-link" className="font-bold transition-colors hover:text-[#b8922a]" style={{ color: GOLD }}>
            Post-Construction Cleaning in Naples
          </Link>
        </p>
      </div>
    </section>
  );
};

const WhyUsIntro = () => (
  <div className="lg:sticky lg:top-24">
    <SectionBadge>Why Naples Trusts Us</SectionBadge>
    <h2 className="text-3xl md:text-4xl font-bold mb-5 leading-tight" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
      Why Naples Trusts Cindyrella
    </h2>
    <p className="text-base mb-8 leading-relaxed" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
      We built our reputation one spotless space at a time. Here's what
      makes us different from every other cleaning service in Naples.
    </p>
    <div className="p-6" style={{ background: NAVY, borderLeft: `4px solid ${GOLD}` }}>
      <p className="text-sm italic leading-relaxed" style={{ color: "rgba(255,255,255,0.9)", fontFamily: FONT_BODY }}>
        "Our standard is simple: leave every space better than we found
        it, every single time."
      </p>
      <p className="text-xs mt-3 font-bold tracking-wider" style={{ color: GOLD, fontFamily: FONT_HEAD }}>
        — Cindyrella Cleaning LLC
      </p>
    </div>
    <Link
      to="/about"
      data-testid="why-us-about-link"
      className="inline-flex items-center gap-2 mt-8 px-6 py-3 text-sm font-bold border-2 transition-all duration-300 hover:bg-[#1B3A6B] hover:text-white"
      style={{ borderColor: NAVY, color: NAVY, fontFamily: FONT_BODY }}
    >
      Our Mission &amp; Values
      <ArrowRight size={16} />
    </Link>
  </div>
);

const TrustPointsList = () => (
  <div>
    {TRUST_POINTS.map(({ Icon, title, description }, idx) => (
      <div
        key={title}
        data-testid={`trust-point-${idx}`}
        className="flex gap-5 py-8"
        style={{
          borderBottom: idx < TRUST_POINTS.length - 1 ? "1px solid rgba(27,58,107,0.08)" : "none",
        }}
      >
        <div
          className="flex-shrink-0 w-12 h-12 flex items-center justify-center"
          style={{ background: "rgba(212,168,67,0.15)", borderRadius: "2px" }}
        >
          <Icon size={22} style={{ color: GOLD }} />
        </div>
        <div>
          <h3 className="text-lg font-bold mb-2" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
            {title}
          </h3>
          <p className="text-sm leading-relaxed" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
            {description}
          </p>
        </div>
      </div>
    ))}
  </div>
);

const WhyUsSection = () => {
  const ref = useScrollReveal();
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      ref={ref}
      className="py-20 md:py-28 section-hidden"
      style={{ background: "#FFFFFF" }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <WhyUsIntro />
          <TrustPointsList />
        </div>
      </div>
    </section>
  );
};

const TestimonialCard = ({ testimonial, idx }) => (
  <div
    data-testid={`testimonial-card-${idx}`}
    className="p-8"
    style={{
      background: "rgba(255,255,255,0.07)",
      borderLeft: `4px solid ${GOLD}`,
      backdropFilter: "blur(10px)",
    }}
  >
    <StarRating />
    <p className="text-sm mt-5 mb-6 leading-relaxed italic" style={{ color: "rgba(255,255,255,0.9)", fontFamily: FONT_BODY }}>
      "{testimonial.text}"
    </p>
    <div>
      <div className="font-bold text-white text-sm" style={{ fontFamily: FONT_HEAD }}>
        {testimonial.name}
      </div>
      <div className="text-xs mt-1" style={{ color: GOLD, fontFamily: FONT_BODY }}>
        {testimonial.role} · {testimonial.location}
      </div>
    </div>
  </div>
);

const watchDesktop = (setVisible) => {
  const mq = window.matchMedia("(min-width: 768px)");
  const update = () => setVisible(mq.matches ? 3 : 1);
  update();
  mq.addEventListener("change", update);
  return () => mq.removeEventListener("change", update);
};

const advance = (maxIdx) => (i) => (i >= maxIdx ? 0 : i + 1);
const retreat = (maxIdx) => (i) => (i <= 0 ? maxIdx : i - 1);
const clampTo = (maxIdx) => (i) => Math.min(i, maxIdx);

const autoAdvance = (setIdx, maxIdx) => {
  const timer = setInterval(() => setIdx(advance(maxIdx)), 5000);
  return () => clearInterval(timer);
};

const useCarousel = (length) => {
  const [visible, setVisible] = useState(3);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const maxIdx = Math.max(0, length - visible);

  useEffect(() => watchDesktop(setVisible), [setVisible]);
  useEffect(() => (paused ? undefined : autoAdvance(setIdx, maxIdx)), [paused, maxIdx, setIdx]);
  useEffect(() => setIdx(clampTo(maxIdx)), [maxIdx, setIdx]);

  const prev = () => setIdx(retreat(maxIdx));
  const next = () => setIdx(advance(maxIdx));
  return { idx, setIdx, maxIdx, prev, next, setPaused, visible };
};

const CarouselArrow = ({ onClick, Icon, testid, label }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    aria-label={label}
    className="w-11 h-11 flex items-center justify-center transition-all duration-200 hover:scale-110"
    style={{ background: "rgba(255,255,255,0.1)", border: `1px solid rgba(212,168,67,0.4)` }}
  >
    <Icon size={18} style={{ color: GOLD }} />
  </button>
);

const CarouselControls = ({ idx, setIdx, maxIdx, prev, next }) => (
  <div className="flex items-center justify-center gap-6 mt-10">
    <CarouselArrow onClick={prev} Icon={ChevronLeft} testid="reviews-prev-btn" label="Previous reviews" />
    <div className="flex gap-2.5">
      {Array.from({ length: maxIdx + 1 }).map((_, i) => (
        <button
          key={`dot-${i}`}
          onClick={() => setIdx(i)}
          data-testid={`reviews-dot-${i}`}
          aria-label={`Go to review slide ${i + 1}`}
          className="h-2.5 transition-all duration-300"
          style={{
            width: i === idx ? "26px" : "10px",
            background: i === idx ? GOLD : "rgba(255,255,255,0.3)",
            borderRadius: "999px",
          }}
        />
      ))}
    </div>
    <CarouselArrow onClick={next} Icon={ChevronRight} testid="reviews-next-btn" label="Next reviews" />
  </div>
);

const CarouselTrack = ({ idx, visible, setPaused }) => (
  <div
    className="overflow-hidden"
    data-testid="reviews-carousel"
    onMouseEnter={() => setPaused(true)}
    onMouseLeave={() => setPaused(false)}
  >
    <div
      className="flex transition-transform duration-700 ease-in-out"
      style={{
        width: `${(TESTIMONIALS.length * 100) / visible}%`,
        transform: `translateX(-${(idx * 100) / TESTIMONIALS.length}%)`,
      }}
    >
      {TESTIMONIALS.map((t, i) => (
        <div key={t.name} className="flex-shrink-0 px-3" style={{ width: `${100 / TESTIMONIALS.length}%` }}>
          <TestimonialCard testimonial={t} idx={i} />
        </div>
      ))}
    </div>
  </div>
);

const TestimonialsSection = () => {
  const ref = useScrollReveal();
  const { idx, setIdx, maxIdx, prev, next, setPaused, visible } = useCarousel(TESTIMONIALS.length);
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      ref={ref}
      className="py-20 md:py-28 section-hidden"
      style={{ background: NAVY }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <SectionBadge dark>Client Reviews</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: FONT_HEAD }}>
            What Our Clients Say
          </h2>
          <p className="mt-4 text-base max-w-xl mx-auto" style={{ color: "rgba(255,255,255,0.65)", fontFamily: FONT_BODY }}>
            Don't take our word for it — hear from the families, hosts, and
            businesses who trust us every week.
          </p>
        </div>
        <CarouselTrack idx={idx} visible={visible} setPaused={setPaused} />
        <CarouselControls idx={idx} setIdx={setIdx} maxIdx={maxIdx} prev={prev} next={next} />
      </div>
    </section>
  );
};

const EstimateBanner = () => (
  <section data-testid="estimate-banner" className="py-12" style={{ background: "linear-gradient(90deg, rgba(212,168,67,0.12), rgba(212,168,67,0.05))", borderTop: "1px solid rgba(212,168,67,0.25)", borderBottom: "1px solid rgba(212,168,67,0.25)" }}>
    <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
      <div>
        <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
          Want a number fast?
        </h2>
        <p className="text-base mt-1" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
          Build your free estimate in 60 seconds — we'll reply within 24 hours.
        </p>
      </div>
      <Link
        to="/estimate"
        data-testid="estimate-banner-btn"
        className="flex items-center gap-2 px-8 py-4 text-base font-bold transition-all duration-300 hover:opacity-90 hover:shadow-lg whitespace-nowrap"
        style={{ background: GOLD, color: NAVY, fontFamily: FONT_BODY }}
      >
        Start My Free Estimate
        <ArrowRight size={18} />
      </Link>
    </div>
  </section>
);

export default function HomePage() {
  useHashScroll(useLocation().hash);
  return (
    <>
      <HeroSection />
      <ServicesPreview />
      <PhotoReportSection lang="en" testid="home-photo-report" />
      <AreasSection lang="en" />
      <EstimateBanner />
      <WhyUsSection />
      <TestimonialsSection />
      <FaqSection />
      <CTABanner testid="home-cta-banner" />
    </>
  );
}
